import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useQueryPortalFolders } from '@hooks/portal/folders/useQueryPortalFolders';

import { portalFolders as portalFoldersSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { resetPortalFolders } from '@slices/portalFolders';

import { isEmpty } from '@utils/common';

import { portalSearchQueries } from '@components/routes/AdminPanel/ProjectProfile/constants';

import FilesListTable from './FilesListTable';
import FolderTree from './FolderTree';
import { checkFolderExists, getFolderByFolderId } from './utils';

import styles from './Files.module.scss';

interface IFilesProps {
	projectId: string;
	isProjectActive: boolean;
	isPortalAdmin: boolean;
}

const Files = ({
	projectId,
	isProjectActive,
	isPortalAdmin,
}: IFilesProps) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();

	const { searchValue, milestones } = useAppSelector(portalFoldersSelector);

	const [pendingFolderId, setPendingFolderId] = useState<string | null>(null);

	const selectedFolderId = searchParams.get(portalSearchQueries.folder);

	const selectFolder = useCallback((id: string) => {
		setSearchParams({ [portalSearchQueries.folder]: id });
	}, [setSearchParams]);

	const {
		portalFolders,
		isLoadingPortalFolders,
		defaultFolderId,
	} = useQueryPortalFolders({
		projectId,
		searchValue,
		milestoneIds: milestones,
	});

	const removeSearchParams = useCallback(() => {
		navigate(pathname, { replace: true });
	}, [pathname, navigate]);

	useEffect(() => {
		if (pendingFolderId && checkFolderExists(portalFolders, pendingFolderId)) {
			selectFolder(pendingFolderId);
			setPendingFolderId(null);
		}
	}, [pendingFolderId, portalFolders, selectFolder]);

	useEffect(() => {
		if (!selectedFolderId && defaultFolderId) {
			selectFolder(defaultFolderId);

			return;
		}

		const isFolderExists = !!(selectedFolderId && checkFolderExists(portalFolders, selectedFolderId));

		if (selectedFolderId && !isFolderExists) {
			if (defaultFolderId) {
				selectFolder(defaultFolderId);
			} else {
				removeSearchParams();
			}
		}
	}, [selectedFolderId, portalFolders, defaultFolderId, selectFolder, removeSearchParams]);

	const selectedFolder = useMemo(() => {
		if (!selectedFolderId || !portalFolders) {
			return;
		}

		return getFolderByFolderId(portalFolders, selectedFolderId);
	}, [selectedFolderId, portalFolders]);

	useEffect(() => {
		return () => {
			removeSearchParams();

			dispatch(resetPortalFolders());
		};
	}, [removeSearchParams, dispatch]);

	const isSingleFolder = portalFolders.length === 1;

	return (
		<div className={styles.filesTabContainer}>
			<FolderTree
				folders={portalFolders}
				isLoading={isLoadingPortalFolders}
				isPortalAdmin={isPortalAdmin}
				isProjectActive={isProjectActive}
				isSingleFolder={isSingleFolder}
				projectId={projectId}
				selectFolder={selectFolder}
				selectedFolderId={selectedFolderId}
				setPendingFolderId={setPendingFolderId}
			/>
			{isEmpty(portalFolders) && !isLoadingPortalFolders
				? <div className={styles.emptyText}>
					Create folder to start uploading files
				</div>
				: <div className={styles.filesListContainer}>
					<FilesListTable
						isPortalAdmin={isPortalAdmin}
						isProjectActive={isProjectActive}
						isSingleFolder={isSingleFolder}
						projectId={projectId}
						selectedFolder={selectedFolder}
					/>
				</div>
			}
		</div>
	);
};

export default Files;
